import { useToast } from '../../hooks/useToast';
import type { FilterModification } from '../ai-search/adaptAiSearchFiltersForPage';

const TOAST_ID = 'ai-search-filter-compatibility';

export const useAiSearchCompatibilityToast = () => {
  const toast = useToast();

  const showCompatibilityToast = (modifications: FilterModification[]) => {
    if (modifications.length === 0 || toast.isActive(TOAST_ID)) return;

    const messages: string[] = [];

    const condensed = modifications.filter((m) => m.type === 'condensed_to_or');
    const removed = modifications.filter(
      (m) => m.type === 'removed_not_filter'
    );
    const limited = modifications.filter(
      (m) => m.type === 'limited_primary_selections'
    );
    const limitedInstances = modifications.filter(
      (m) => m.type === 'limited_filter_instances'
    );
    const normalizedGranularity = modifications.filter(
      (m) => m.type === 'normalized_filter_granularity'
    );

    const uniqueNames = (items: FilterModification[]) =>
      Array.from(new Set(items.map((item) => item.filterName)));

    if (condensed.length > 0) {
      messages.push(
        `Combined multiple ${uniqueNames(condensed).join(
          ', '
        )} filters so they work together on this page`
      );
    }
    if (removed.length > 0) {
      messages.push(
        `Removed ${uniqueNames(removed).join(
          ', '
        )} exclusions because this page doesn't support them`
      );
    }
    if (limited.length > 0) {
      messages.push(
        `This page limits ${uniqueNames(limited).join(
          ', '
        )} selections, so we kept the first ones`
      );
    }
    if (limitedInstances.length > 0) {
      const names = uniqueNames(limitedInstances);
      if (names.length === 1) {
        messages.push(
          `This page supports one ${names[0]} filter, so we kept the first one`
        );
      } else {
        messages.push(
          `This page supports one filter per ${names.join(
            ', '
          )}, so we kept the first ones`
        );
      }
    }
    if (normalizedGranularity.length > 0) {
      messages.push(
        `Standardized ${uniqueNames(normalizedGranularity).join(
          ', '
        )} selections to a single granularity level because this page requires it`
      );
    }

    toast({
      title: 'We adjusted a few filters',
      description: messages.join('. '),
      status: 'info',
      variant: 'subtle',
      duration: 6000,
      isClosable: true,
      position: 'top-right',
      id: TOAST_ID,
    });
  };

  return showCompatibilityToast;
};
